import type React from 'react';
import { ChevronDown, ChevronRight, UserCheck, Clock, CheckCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface CategoryStats {
  totalConfirmed: number;
  totalWaiting: number;
  totalDeclined: number;
  totalNeeded: number;
}

interface CategoryHeaderProps {
  name: string;
  stats: CategoryStats;
  depth: number;
  isExpanded: boolean;
  onToggle: () => void;
  hasChildren?: boolean;
  hasTalents?: boolean;
  className?: string;
}

function StatusCount({
  icon,
  count,
  word,
  colorClass,
}: {
  icon: React.ReactNode;
  count: number;
  word: string;
  colorClass: string;
}) {
  const label = `${count} ${word}`;
  return (
    <span className="inline-flex items-center gap-1" title={label}>
      <span className={cn(count > 0 && colorClass)}>{icon}</span>
      <span className={cn('font-medium', count > 0 && colorClass)}>{count}</span>
      <span className="sr-only sm:not-sr-only">{word}</span>
    </span>
  );
}

export function CategoryHeader({
  name,
  stats,
  depth,
  isExpanded,
  onToggle,
  hasChildren = false,
  hasTalents = false,
  className
}: CategoryHeaderProps) {
  const hasNeed = stats.totalNeeded > 0;
  const isComplete = hasNeed && stats.totalConfirmed >= stats.totalNeeded;
  const completionPercent = Math.round((stats.totalConfirmed / stats.totalNeeded) * 100 || 0);
  
  const getBgGradient = () => {
    if (depth === 0) return 'from-primary/5 to-background hover:from-primary/10 hover:to-primary/5';
    if (depth === 1) return 'from-purple-50 to-background hover:from-purple-100 hover:to-purple-50';
    return 'from-indigo-50 to-background hover:from-indigo-100 hover:to-indigo-50';
  };

  const getTextSize = () => {
    if (depth === 0) return 'text-xl';
    return 'text-base';
  };

  const getIconSize = () => {
    if (depth === 0) return 24;
    return 20;
  };

  return (
    <div 
      onClick={onToggle}
      className={cn(
        'cursor-pointer transition-all w-full bg-gradient-to-r',
        depth === 0 ? 'p-3 sm:p-4' : 'p-2 sm:p-3',
        getBgGradient(),
        className
      )}
    >
      {/* Mobile: Stack vertikalt, Desktop: Horisontal */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Button 
            variant="ghost" 
            size="icon"
            className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-background/80 shrink-0"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5" size={getIconSize()} />
            ) : (
              <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" size={getIconSize()} />
            )}
          </Button>
          
          <h3 className={cn('font-bold text-foreground truncate', depth === 0 ? 'text-base sm:text-xl' : 'text-sm sm:text-base')}>
            {name}
            {/* Skjul meta-info på mobil */}
            {hasChildren && (
              <span className="hidden sm:inline ml-2 text-sm text-muted-foreground font-normal">
                (sub-kategorier)
              </span>
            )}
            {hasTalents && (
              <span className="hidden sm:inline ml-2 text-sm text-muted-foreground font-normal">
                (talenter)
              </span>
            )}
          </h3>
        </div>

        {/* Statusoversikt - kun visning, kun på rot-nivå */}
        {depth === 0 && (
          <div className="flex items-center gap-3 sm:gap-4 flex-wrap text-xs sm:text-sm text-muted-foreground">
            <StatusCount
              icon={<UserCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />}
              count={stats.totalConfirmed}
              word="bekreftet"
              colorClass="text-status-confirmed"
            />
            <StatusCount
              icon={<Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />}
              count={stats.totalWaiting}
              word="venter"
              colorClass="text-status-waiting"
            />
            {stats.totalDeclined > 0 && (
              <StatusCount
                icon={<X className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />}
                count={stats.totalDeclined}
                word="avslått"
                colorClass="text-status-declined"
              />
            )}

            {hasNeed ? (
              <div className="flex items-center gap-2">
                <div
                  role="progressbar"
                  aria-label="Bemannet"
                  aria-valuenow={Math.min(completionPercent, 100)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  className="w-16 sm:w-20 h-1.5 rounded-full bg-muted overflow-hidden"
                >
                  <div
                    className="h-full rounded-full bg-status-confirmed"
                    style={{ width: `${Math.min(completionPercent, 100)}%` }}
                  />
                </div>
                {isComplete ? (
                  <span className="inline-flex items-center gap-1 text-status-confirmed font-medium">
                    <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
                    100 %
                  </span>
                ) : (
                  <span className="font-medium">{completionPercent} %</span>
                )}
              </div>
            ) : (
              <span>Ingen behov</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

